import type { AxiosInstance } from 'axios';

export type SupplierStatus = 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
export type PurchaseOrderStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'PARTIAL_RECEIVED'
  | 'RECEIVED'
  | 'CANCELLED'
  | 'CLOSED';
export type SupplierBillStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'PAID'
  | 'PARTIALLY_PAID'
  | 'DISPUTED'
  | 'CANCELLED';
export type ExpenseStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'PAID' | 'REJECTED';
export type ExpenseCategory =
  | 'OFFICE'
  | 'UTILITIES'
  | 'RENT'
  | 'SOFTWARE'
  | 'MARKETING'
  | 'TRAVEL'
  | 'PROFESSIONAL_FEES'
  | 'REPAIRS'
  | 'OTHER';
export type DamageReason = 'TRANSIT' | 'HANDLING' | 'EXPIRY' | 'THEFT' | 'OTHER';

export interface Supplier {
  id: string;
  name: string;
  legalName: string | null;
  email: string | null;
  phone: string | null;
  gstin: string | null;
  panNumber: string | null;
  city: string | null;
  stateCode: string | null;
  pincode: string | null;
  paymentTermsDays: number;
  status: SupplierStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InternalWarehouse {
  id: string;
  code: string;
  name: string;
  city: string;
  stateCode: string;
  pincode: string;
  isDefault: boolean;
  active: boolean;
  createdAt: string;
}

export interface PurchaseOrderItem {
  id: string;
  description: string;
  sku: string | null;
  hsnSac: string | null;
  qty: number;
  unitCostPaise: number;
  gstRate: number;
  taxPaise: number;
  lineTotalPaise: number;
  receivedQty: number;
}

export interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierId: string;
  warehouseId: string | null;
  status: PurchaseOrderStatus;
  itemsSubtotalPaise: number;
  taxPaise: number;
  shippingPaise: number;
  discountPaise: number;
  grandTotalPaise: number;
  expectedDeliveryAt: string | null;
  approvedAt: string | null;
  cancelledAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderDetail extends PurchaseOrder {
  items: PurchaseOrderItem[];
}

export interface InventoryBatch {
  id: string;
  warehouseId: string;
  itemKey: string;
  itemDescription: string;
  batchCode: string;
  qtyReceived: number;
  qtyOnHand: number;
  unitCostPaise: number;
  expiryAt: string | null;
  createdAt: string;
}

export interface WarehouseValuation {
  warehouseId: string;
  itemCount: number;
  totalQty: number;
  totalValuePaise: number;
}

export interface SupplierBill {
  id: string;
  billNumber: string;
  supplierInvoiceNo: string;
  supplierId: string;
  purchaseOrderId: string | null;
  status: SupplierBillStatus;
  subtotalPaise: number;
  taxPaise: number;
  totalPaise: number;
  paidPaise: number;
  dueAt: string;
  approvedAt: string | null;
  paidAt: string | null;
  notes: string | null;
  createdAt: string;
}

export interface Expense {
  id: string;
  refNumber: string;
  category: ExpenseCategory;
  description: string;
  amountPaise: number;
  taxPaise: number;
  totalPaise: number;
  status: ExpenseStatus;
  payeeName: string | null;
  receiptFileId: string | null;
  incurredAt: string;
  approvedAt: string | null;
  paidAt: string | null;
  notes: string | null;
  createdAt: string;
}

export interface CreateSupplierRequest {
  name: string;
  legalName?: string;
  email?: string;
  phone?: string;
  gstin?: string;
  panNumber?: string;
  city?: string;
  stateCode?: string;
  pincode?: string;
  paymentTermsDays?: number;
  notes?: string;
}

export interface POItemInput {
  description: string;
  sku?: string;
  hsnSac?: string;
  qty: number;
  unitCostPaise: number;
  gstRate?: number;
}

export interface CreatePurchaseOrderRequest {
  supplierId: string;
  warehouseId?: string;
  expectedDeliveryAt?: string;
  shippingPaise?: number;
  discountPaise?: number;
  notes?: string;
  items: POItemInput[];
}

export interface CreateSupplierBillRequest {
  supplierId: string;
  purchaseOrderId?: string;
  supplierInvoiceNo: string;
  subtotalPaise: number;
  taxPaise?: number;
  dueAt?: string;
  notes?: string;
}

export interface CreateExpenseRequest {
  category: ExpenseCategory;
  description: string;
  amountPaise: number;
  taxPaise?: number;
  payeeName?: string;
  incurredAt: string;
  notes?: string;
}

export function erpEndpoints(http: AxiosInstance) {
  return {
    // Suppliers
    listSuppliers: () =>
      http.get<{ data: Supplier[] }>('/admin/erp/suppliers').then((r) => r.data.data),
    createSupplier: (body: CreateSupplierRequest) =>
      http.post<Supplier>('/admin/erp/suppliers', body).then((r) => r.data),
    updateSupplier: (
      supplierId: string,
      body: Partial<CreateSupplierRequest> & { status?: SupplierStatus },
    ) => http.patch<Supplier>(`/admin/erp/suppliers/${supplierId}`, body).then((r) => r.data),

    // Warehouses
    listWarehouses: () =>
      http.get<{ data: InternalWarehouse[] }>('/admin/erp/warehouses').then((r) => r.data.data),
    createWarehouse: (body: {
      code: string;
      name: string;
      addressLine1: string;
      city: string;
      stateCode: string;
      pincode: string;
      isDefault?: boolean;
    }) => http.post<InternalWarehouse>('/admin/erp/warehouses', body).then((r) => r.data),

    // Purchase orders
    listPOs: () =>
      http.get<{ data: PurchaseOrder[] }>('/admin/erp/purchase-orders').then((r) => r.data.data),
    getPO: (poId: string) =>
      http.get<PurchaseOrderDetail>(`/admin/erp/purchase-orders/${poId}`).then((r) => r.data),
    createPO: (body: CreatePurchaseOrderRequest) =>
      http.post<PurchaseOrderDetail>('/admin/erp/purchase-orders', body).then((r) => r.data),
    submitPO: (poId: string) =>
      http.post<PurchaseOrder>(`/admin/erp/purchase-orders/${poId}/submit`).then((r) => r.data),
    approvePO: (poId: string) =>
      http.post<PurchaseOrder>(`/admin/erp/purchase-orders/${poId}/approve`).then((r) => r.data),
    cancelPO: (poId: string) =>
      http.post<PurchaseOrder>(`/admin/erp/purchase-orders/${poId}/cancel`).then((r) => r.data),

    // GRN
    createGRN: (body: {
      purchaseOrderId: string;
      warehouseId: string;
      notes?: string;
      items: {
        purchaseOrderItemId: string;
        qtyReceived: number;
        qtyRejected?: number;
        rejectReason?: string;
      }[];
    }) => http.post('/admin/erp/grns', body).then((r) => r.data),

    // Inventory
    listBatches: () =>
      http.get<{ data: InventoryBatch[] }>('/admin/erp/inventory/batches').then((r) => r.data.data),
    warehouseValuation: (warehouseId: string) =>
      http
        .get<WarehouseValuation>(`/admin/erp/inventory/warehouses/${warehouseId}/valuation`)
        .then((r) => r.data),
    createTransfer: (body: {
      fromWarehouseId: string;
      toWarehouseId: string;
      itemKey: string;
      qty: number;
      notes?: string;
    }) => http.post('/admin/erp/inventory/transfers', body).then((r) => r.data),
    reportDamage: (body: {
      warehouseId: string;
      itemKey: string;
      qty: number;
      reason: DamageReason;
      notes?: string;
    }) => http.post('/admin/erp/inventory/damage', body).then((r) => r.data),

    // Bills
    listBills: () =>
      http.get<{ data: SupplierBill[] }>('/admin/erp/bills').then((r) => r.data.data),
    createBill: (body: CreateSupplierBillRequest) =>
      http.post<SupplierBill>('/admin/erp/bills', body).then((r) => r.data),
    approveBill: (billId: string) =>
      http.post<SupplierBill>(`/admin/erp/bills/${billId}/approve`).then((r) => r.data),
    payBill: (billId: string, body: { amountPaise?: number; bankRef?: string; note?: string }) =>
      http.post<SupplierBill>(`/admin/erp/bills/${billId}/pay`, body).then((r) => r.data),

    // Expenses
    listExpenses: () =>
      http.get<{ data: Expense[] }>('/admin/erp/expenses').then((r) => r.data.data),
    submitExpense: (body: CreateExpenseRequest) =>
      http.post<Expense>('/admin/erp/expenses', body).then((r) => r.data),
    approveExpense: (expenseId: string) =>
      http.post<Expense>(`/admin/erp/expenses/${expenseId}/approve`).then((r) => r.data),
    rejectExpense: (expenseId: string, reason: string) =>
      http.post<Expense>(`/admin/erp/expenses/${expenseId}/reject`, { reason }).then((r) => r.data),
    payExpense: (expenseId: string) =>
      http.post<Expense>(`/admin/erp/expenses/${expenseId}/pay`).then((r) => r.data),
  };
}

export type ErpEndpoints = ReturnType<typeof erpEndpoints>;
